<template>
    <div class="container overflow-auto">
        <div class="toolbar shadow-sm">
            <img src="@/assets/icons/ConfigurationIcon.png" />
            <span class="title">{{ $t("admin.system.publisher_configuration_tool.title") }}</span>
        </div>
        <!-- Search Bar -->
        <div class="publisher-search-container">
            <input
                type="text"
                class="publisher-search-input"
                v-model="searchQuery"
                :placeholder="$t('admin.system.publisher_configuration_tool.search_publishers_placeholder')"
                :readonly="selectedPublisher != null"
            />
            <button
                v-if="selectedPublisher != null"
                class="clear-selection-btn"
                @click="clearPublisherSelection"
                :title="$t('admin.system.publisher_configuration_tool.change_publisher')"
            >
                ✕
            </button>
        </div>

        <!-- Publisher Cards Grid -->
        <div class="publisher-cards-grid" v-if="filteredPublishers && filteredPublishers.length > 0 && selectedPublisher == null">
            <div
                v-for="publisher in filteredPublishers"
                :key="publisher.id"
                class="publisher-card"
                :class="{ 'selected-card': isSelectedPublisher(publisher) }"
                @click="handlePublisherSelected(publisher)"
            >
                <div class="publisher-card-name">{{ publisherName(publisher) }}</div>
                <div class="publisher-card-id">{{ publisher.getId() }}</div>
                <div class="publisher-card-stats">
                    <span class="stat-badge">
                        <span class="stat-icon">👥</span>
                        {{ publisherUserCount(publisher) }} {{ $t('admin.system.publisher_configuration_tool.users_label') }}
                    </span>
                    <span class="stat-badge">
                        <span class="stat-icon">📦</span>
                        {{ publisherBundleCount(publisher) }} {{ $t('admin.system.publisher_configuration_tool.bundles_label') }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div class="publisher-empty-state" v-else-if="searchQuery && filteredPublishers && filteredPublishers.length === 0 && selectedPublisher == null">
            <p>{{ $t('admin.system.publisher_configuration_tool.no_publishers_found') }}</p>
        </div>
        <div class="card" v-if="selectedPublisher != null">
            <h1 class="text-center">{{ publisherName(selectedPublisher) }}</h1>
            <div class="card-body">
                <div>
                    <h3>{{ $t("admin.system.publisher_configuration_tool.ondago_pro_users_dashboard_title") }}</h3>
                    <div class="mb-3">
                        <label for="addNewUserFormControlInput" class="form-label">{{ $t("admin.system.publisher_configuration_tool.ondago_pro_users_dashboard_button_add_title") }}</label>
                        <div class="d-flex">
                            <input type="email" class="form-control me-2" id="addNewUserFormControlInput" :placeholder="$t('admin.system.publisher_configuration_tool.email_placeholder')" v-model="newUserEmailToAdd" @keyup.enter="handleAddUserFromPublisher()">
                            <button type="button" class="btn btn-outline-primary ms-auto" @click="handleAddUserFromPublisher()">{{ $t("admin.system.publisher_configuration_tool.ondago_pro_users_dashboard_button_add") }}</button>
                        </div>
                        <div v-if="emailError" class="invalid-feedback d-block">
                            {{ emailError }}
                        </div>
                        <div v-if="webviewerNotFoundEmail" class="user-not-found-suggestion mt-2">
                            <span>{{ $t('admin.system.publisher_configuration_tool.create_user_suggestion') }}</span>
                            <button
                                type="button"
                                class="btn btn-sm btn-outline-secondary ms-2"
                                :disabled="isCreatingUser"
                                @click="handleCreateAndAddWebviewerUser()">
                                {{ isCreatingUser ? $t('admin.system.publisher_configuration_tool.create_user_creating') : $t('admin.system.publisher_configuration_tool.create_user_button') }}
                            </button>
                        </div>
                    </div>
                    <hr class="my-4">
                    <div v-if="isLoadingWebviewerUsers" class="d-flex justify-content-center align-items-center py-4">
                        <div class="spinner-border spinner-border-sm text-secondary me-2" role="status"></div>
                        <span class="text-muted">{{ $t("admin.system.publisher_configuration_tool.loading_users") }}</span>
                    </div>
                    <template v-else-if="loadedUsersForWebviewer != null && loadedUsersForWebviewer.length > 0">
                        <ul class="list-group">
                            <li class="list-group-item d-flex justify-content-between align-items-center" :class="{ 'modified-row': modifiedWebviewerUsers.has(user.email), 'deleted-row': isWebviewerUserMarkedForDeletion(user.email) }" v-for="user in loadedUsersForWebviewer" v-bind:key="user.email">
                                <span :class="{ 'deleted-text': isWebviewerUserMarkedForDeletion(user.email) }">
                                    <span v-if="modifiedWebviewerUsers.has(user.email) && !isWebviewerUserMarkedForDeletion(user.email)" class="modified-indicator">●</span>
                                    <span v-if="isWebviewerUserMarkedForDeletion(user.email)" class="deleted-indicator">🗑️</span>
                                    {{ user.email }}
                                </span>
                                <button v-if="!isWebviewerUserMarkedForDeletion(user.email)" type="button" class="btn btn-outline-danger" @click="handleRemoveUserFromPublisher(user.email)">{{ $t("admin.system.publisher_configuration_tool.ondago_pro_users_dashboard_button_remove") }}</button>
                                <button v-else type="button" class="btn btn-outline-success" @click="handleUndoRemoveUserFromPublisher(user.email)">{{ $t("admin.system.publisher_configuration_tool.undo_button") }}</button>
                            </li>
                        </ul>
                    </template>
                    <template v-else-if="!isLoadingWebviewerUsers">
                        <p>{{ $t("admin.system.publisher_configuration_tool.ondago_pro_users_dashboard_no_users_label") }}</p>
                    </template>
                    <hr class="my-4">
                    <div class="save-section">
                        <div v-if="hasWebviewerChanges" class="unsaved-changes-banner">
                            <span class="warning-icon">⚠️</span>
                            {{ $t("admin.system.publisher_configuration_tool.unsaved_changes_warning", { count: webviewerChangeCount }) }}
                        </div>
                        <div class="d-flex justify-content-center align-items-center flex-column">
                            <button
                                type="button"
                                :class="['btn', hasWebviewerChanges ? 'btn-warning' : 'btn-success']"
                                :disabled="!hasWebviewerChanges || isSavingWebviewer"
                                @click="handleSaveUsersForPublisher(selectedPublisher.getId())">
                                <span v-if="hasWebviewerChanges">💾 {{ webviewerSaveButtonText }}</span>
                                <span v-else>✓ {{ $t("admin.system.publisher_configuration_tool.no_changes") }}</span>
                            </button>
                            <small v-if="!hasWebviewerChanges" class="text-muted mt-2">{{ $t("admin.system.publisher_configuration_tool.all_saved") }}</small>
                        </div>
                    </div>
                    <div v-if="showAlert" :class="['alert', `alert-${alertType}`, 'my-4']" role="alert">
                        {{ alertMessage }}
                    </div>
                </div>
            </div>
        </div>
        <hr class="my-4" v-if="selectedPublisher != null">
        <div class="card" v-if="selectedPublisher != null">
            <div class="card-body">
                <h3>{{ $t("admin.system.publisher_configuration_tool.ondago_pro_users_section_title") }}</h3>
                <div class="mb-3">
                <label class="form-label">{{ $t("admin.system.publisher_configuration_tool.add_ondago_pro_user_label") }}</label>
                <div class="d-flex">
                    <input
                        type="email"
                        class="form-control me-2"
                        :placeholder="$t('admin.system.publisher_configuration_tool.email_placeholder')"
                        v-model="newOndagoProUserEmail"
                        @keyup.enter="handleAddOndagoProUser()">
                    <button
                        type="button"
                        class="btn btn-outline-primary ms-auto"
                        @click="handleAddOndagoProUser()">
                        {{ $t("admin.system.publisher_configuration_tool.add_ondago_pro_user_button") }}
                    </button>
                </div>
                <div v-if="ondagoProEmailError" class="invalid-feedback d-block">
                    {{ ondagoProEmailError }}
                </div>
                <div v-if="ondagoProNotFoundEmail" class="user-not-found-suggestion mt-2">
                    <span>{{ $t('admin.system.publisher_configuration_tool.create_user_suggestion') }}</span>
                    <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary ms-2"
                        :disabled="isCreatingUser"
                        @click="handleCreateAndAddOndagoProUser()">
                        {{ isCreatingUser ? $t('admin.system.publisher_configuration_tool.create_user_creating') : $t('admin.system.publisher_configuration_tool.create_user_button') }}
                    </button>
                </div>
            </div>
            <hr class="my-4">
            <div v-if="isOndagoProMisconfigured" class="alert alert-warning" role="alert">
                ⚠️ {{ $t("admin.system.publisher_configuration_tool.ondago_pro_misconfigured_warning") }}
            </div>
            <div v-if="isLoadingOndagoProUsers" class="d-flex justify-content-center align-items-center py-4">
                <div class="spinner-border spinner-border-sm text-secondary me-2" role="status"></div>
                <span class="text-muted">{{ $t("admin.system.publisher_configuration_tool.loading_users") }}</span>
            </div>
            <table v-else class="table table-bordered table-hover">
            <thead class="sticky-top">
                <tr class="table-primary">
                    <th>{{ $t('admin.system.publisher_configuration_tool.column_email') }}</th>
                    <th>{{ $t('admin.system.publisher_configuration_tool.column_phone_number') }}</th>
                    <th>{{ $t('admin.system.publisher_configuration_tool.column_emergency') }}</th>
                    <th>{{ $t('admin.system.publisher_configuration_tool.column_terrain_report') }}</th>
                    <th>{{ $t('admin.system.publisher_configuration_tool.column_in_app_notification') }}</th>
                    <th>{{ $t('admin.system.publisher_configuration_tool.column_email_notification') }}</th>
                    <th>{{ $t('admin.system.publisher_configuration_tool.column_sms_notification') }}</th>
                    <th>{{ $t('admin.system.publisher_configuration_tool.column_ondago_plus') }}</th>
                    <th>{{ $t('admin.system.publisher_configuration_tool.permissions.label') }}</th>
                    <th>{{ $t('admin.system.publisher_configuration_tool.column_actions') }}</th>
                </tr>
            </thead>
                <tbody v-if="loadedUsersForOndagoPro != null && loadedUsersForOndagoPro.length > 0">
                    <tr v-for="(user, index) in loadedUsersForOndagoPro" :key="index" :class="{ 'modified-row': isOndagoProUserModified(user) && !isOndagoProUserMarkedForDeletion(user), 'deleted-row': isOndagoProUserMarkedForDeletion(user) }">
                        <td :class="{ 'deleted-text': isOndagoProUserMarkedForDeletion(user) }">
                            <span v-if="isOndagoProUserModified(user) && !isOndagoProUserMarkedForDeletion(user)" class="modified-indicator">●</span>
                            <span v-if="isOndagoProUserMarkedForDeletion(user)" class="deleted-indicator">🗑️</span>
                            {{user.email}}
                        </td>
                        <td :class="{ 'deleted-text': isOndagoProUserMarkedForDeletion(user) }">
                            <input
                                type="text"
                                :class="['form-control', { 'is-invalid': user.phoneNumber && !isValidPhoneNumber(user.phoneNumber) }]"
                                v-model="user.phoneNumber"
                                :disabled="isOndagoProUserMarkedForDeletion(user)"
                                placeholder="+14165551234 or 4165551234"
                                @input="markOndagoProUserAsModified(user)">
                            <div v-if="user.phoneNumber && !isValidPhoneNumber(user.phoneNumber)" class="invalid-feedback d-block">
                                {{ $t('admin.system.publisher_configuration_tool.error_phone_number_invalid') }}
                            </div>
                        </td>
                        <td :class="{ 'deleted-text': isOndagoProUserMarkedForDeletion(user) }">
                            <div class="form-check form-switch">
                                <input class="form-check-input" type="checkbox" :checked="verifyUserGroupId(user, 'emergency')" :disabled="isOndagoProUserMarkedForDeletion(user)" @change="handleUserGroupIdChange(user, 'emergency', $event); markOndagoProUserAsModified(user)">
                            </div>
                        </td>
                        <td :class="{ 'deleted-text': isOndagoProUserMarkedForDeletion(user) }">
                            <div class="form-check form-switch">
                                <input class="form-check-input" type="checkbox" :checked="verifyUserGroupId(user, 'terrain_report')" :disabled="isOndagoProUserMarkedForDeletion(user)" @change="handleUserGroupIdChange(user, 'terrain_report', $event); markOndagoProUserAsModified(user)">
                            </div>
                        </td>
                        <td :class="{ 'deleted-text': isOndagoProUserMarkedForDeletion(user) }">
                            <div class="form-check form-switch">
                                <input class="form-check-input" type="checkbox" v-model="user.notifyViaAppNotification" :disabled="isOndagoProUserMarkedForDeletion(user)" @change="markOndagoProUserAsModified(user)">
                            </div>
                        </td>
                        <td :class="{ 'deleted-text': isOndagoProUserMarkedForDeletion(user) }">
                            <div class="form-check form-switch">
                                <input class="form-check-input" type="checkbox" v-model="user.notifyViaEmail" :disabled="isOndagoProUserMarkedForDeletion(user)" @change="markOndagoProUserAsModified(user)">
                            </div>
                        </td>
                        <td :class="{ 'deleted-text': isOndagoProUserMarkedForDeletion(user) }">
                            <div class="form-check form-switch">
                                <input class="form-check-input" type="checkbox" v-model="user.notifyViaSMS" :disabled="isOndagoProUserMarkedForDeletion(user)" @change="markOndagoProUserAsModified(user)">
                            </div>
                        </td>
                        <td :class="{ 'deleted-text': isOndagoProUserMarkedForDeletion(user) }">
                            <div class="form-check form-switch">
                                <input class="form-check-input" type="checkbox" :checked="ondagoPlusMap[user.id] || false" :disabled="isOndagoProUserMarkedForDeletion(user)" @change="handleOndagoPlusChange(user, $event)">
                            </div>
                        </td>
                        <td :class="{ 'deleted-text': isOndagoProUserMarkedForDeletion(user) }">
                            <div class="dropdown">
                                <button class="btn btn-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" :disabled="isOndagoProUserMarkedForDeletion(user)">
                                    {{ userRoleString(user) }}
                                </button>
                                <ul class="dropdown-menu">
                                    <li><a class="dropdown-item" href="#" @click.prevent="handleRoleChange(user, 'admin')">{{ $t('admin.system.publisher_configuration_tool.permissions.admin') }}</a></li>
                                    <li><a class="dropdown-item" href="#" @click.prevent="handleRoleChange(user, 'member')">{{ $t('admin.system.publisher_configuration_tool.permissions.member') }}</a></li>
                                    <li><a class="dropdown-item" href="#" @click.prevent="handleRoleChange(user, 'guest')">{{ $t('admin.system.publisher_configuration_tool.permissions.guest') }}</a></li>
                                </ul>
                            </div>
                        </td>
                        <td>
                            <button v-if="!isOndagoProUserMarkedForDeletion(user)" type="button" class="btn btn-outline-danger btn-sm" @click="handleRemoveOndagoProUser(user)">
                                {{ $t("admin.system.publisher_configuration_tool.ondago_pro_users_dashboard_button_remove") }}
                            </button>
                            <button v-else type="button" class="btn btn-outline-success btn-sm" @click="handleUndoRemoveOndagoProUser(user)">
                                {{ $t("admin.system.publisher_configuration_tool.undo_button") }}
                            </button>
                        </td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr><td colspan="10">{{ $t("admin.system.publisher_configuration_tool.ondago_pro_users_dashboard_no_users_label") }}</td></tr>
                </tbody>
            </table>
            <hr class="my-4">
            <div class="save-section">
                <div v-if="hasOndagoProChanges" class="unsaved-changes-banner">
                    <span class="warning-icon">⚠️</span>
                    {{ $t("admin.system.publisher_configuration_tool.unsaved_changes_warning", { count: ondagoProChangeCount }) }}
                </div>
                <div class="d-flex justify-content-center align-items-center flex-column">
                    <button
                        type="button"
                        :class="['btn', hasOndagoProChanges ? 'btn-warning' : 'btn-success']"
                        :disabled="!hasOndagoProChanges || isSavingOndagoPro || isOndagoProMisconfigured"
                        @click="handleSaveOndagoProUsers()">
                        <span v-if="hasOndagoProChanges">💾 {{ ondagoProSaveButtonText }}</span>
                        <span v-else>✓ {{ $t("admin.system.publisher_configuration_tool.no_changes") }}</span>
                    </button>
                    <small v-if="!hasOndagoProChanges" class="text-muted mt-2">{{ $t("admin.system.publisher_configuration_tool.all_saved") }}</small>
                </div>
            </div>
            <div v-if="showOndagoProAlert" :class="['alert', `alert-${ondagoProAlertType}`, 'my-4']" role="alert">
                {{ ondagoProAlertMessage }}
            </div>
            </div>
        </div>
    </div>
</template>

<script>
import PublisherService from "@/service/PublisherService.js";
import UserService from "@/service/UserService";
import AuthenticationService from "@/service/AuthenticationService";
import LangUtils from "@/utils/LangUtils.js";
import SecurityService from "@/service/SecurityService";
import { realtimeDatabase } from "@/firebaseConfig";

export default {    
    data() {
        return {
            publisherService: new PublisherService(),
            userService: new UserService(),
            authenticationService: new AuthenticationService(),
            securityService: new SecurityService(),
            pageTitle: null,
            publishers: null,
            selectedPublisher: null,
            usersForOndagoProRoles: [],
            loadedUsersForWebviewer: [],
            loadedUsersForOndagoPro: [],
            usersRolesForOndagoPro: {},
            ondagoPlusMap: {},
            ondagoPlusChanges: {},
            newUserEmailToAdd: null,
            emailError: '',
            newOndagoProUserEmail: '',
            ondagoProEmailError: '',
            showAlert: false,
            alertMessage: "",
            alertType: "",
            showOndagoProAlert: false,
            ondagoProAlertMessage: "",
            ondagoProAlertType: "",
            userRoleChanges: {},
            // Change tracking
            hasWebviewerChanges: false,
            hasOndagoProChanges: false,
            modifiedWebviewerUsers: new Set(),
            modifiedOndagoProUsers: new Set(),
            originalWebviewerUsers: [],
            originalOndagoProUsers: [],
            markedForDeletionWebviewer: new Set(),
            markedForDeletionOndagoPro: new Set(),
            searchQuery: '',
            webviewerNotFoundEmail: null,
            ondagoProNotFoundEmail: null,
            isCreatingUser: false,
            isLoadingOndagoProUsers: false,
            isLoadingWebviewerUsers: false,
            isSavingWebviewer: false,
            isSavingOndagoPro: false
        }
    },

    computed: {
        filteredPublishers() {
            if (!this.publishers || !this.searchQuery) {
                return this.publishers;
            }
            const query = this.searchQuery.toLowerCase().trim();
            return this.publishers.filter(p => {
                const name = (this.publisherName(p) || '').toLowerCase();
                const id = (p.getId() || '').toLowerCase();
                return name.includes(query) || id.includes(query);
            });
        },
        webviewerChangeCount() {
            return this.modifiedWebviewerUsers.size;
        },
        ondagoProChangeCount() {
            return this.modifiedOndagoProUsers.size;
        },
        webviewerSaveButtonText() {
            if (this.hasWebviewerChanges) {
                return this.$t("admin.system.publisher_configuration_tool.save_with_changes", { count: this.webviewerChangeCount });
            }
            return this.$t("admin.system.publisher_configuration_tool.ondago_pro_users_dashboard_button_save");
        },
        ondagoProSaveButtonText() {
            if (this.hasOndagoProChanges) {
                return this.$t("admin.system.publisher_configuration_tool.save_with_changes", { count: this.ondagoProChangeCount });
            }
            return this.$t("admin.system.publisher_configuration_tool.ondago_pro_users_dashboard_button_save");
        },
        isOndagoProMisconfigured() {
            if (!this.selectedPublisher) return false;
            const groups = this.selectedPublisher.getContactGroupByPOIType();
            return !groups || groups.length === 0;
        }
    },

    watch: {
        newUserEmailToAdd() {
            this.webviewerNotFoundEmail = null;
        },
        newOndagoProUserEmail() {
            this.ondagoProNotFoundEmail = null;
        }
    },

    created() {
        this._loadAllPublishers();
    },

    methods: {
    
        publisherName(publisher) {
            const name = LangUtils.valueForLanguage(publisher.getName(), this.$i18n.locale);
            if (name) {
                return name;
            } else {
                return publisher.getId();
            }
        },

        isSelectedPublisher(publisher) {
            return this.selectedPublisher?.getId() === publisher.getId();
        },

        publisherUserCount(publisher) {
            const userIds = publisher.getUsersIds();
            return userIds ? userIds.length : 0;
        },

        publisherBundleCount(publisher) {
            const bundleIds = publisher.getBundleIds();
            return bundleIds ? Object.keys(bundleIds).length : 0;
        },

        userRoleString(user) {
            // Check if there's a pending role change
            if (this.userRoleChanges[user.id]) {
                const role = this.userRoleChanges[user.id];
                if (role === 'admin') {
                    return this.$t('admin.system.publisher_configuration_tool.permissions.admin');
                } else if (role === 'member') {
                    return this.$t('admin.system.publisher_configuration_tool.permissions.member');
                } else if (role === 'guest') {
                    return this.$t('admin.system.publisher_configuration_tool.permissions.guest');
                }
            }

            // Otherwise use the loaded role
            if (user && this.usersRolesForOndagoPro) {
                const role = this.findRolesByUserId(user.id);
                if (role) {
                    if (role === 'admin') {
                        return this.$t('admin.system.publisher_configuration_tool.permissions.admin');
                    } else if (role === 'member') {
                        return this.$t('admin.system.publisher_configuration_tool.permissions.member');
                    } else if (role === 'guest') {
                        return this.$t('admin.system.publisher_configuration_tool.permissions.guest');
                    }
                    return role;
                }
            }
            return this.$t('admin.system.publisher_configuration_tool.permissions.noRole');
        },

        handleRoleChange(user, role) {
            this.userRoleChanges[user.id] = role;
            this.markOndagoProUserAsModified(user);
        },

        handleOndagoPlusChange(user, event) {
            const enabled = event.target.checked;
            this.$set(this.ondagoPlusMap, user.id, enabled);
            this.$set(this.ondagoPlusChanges, user.id, enabled);
            this.markOndagoProUserAsModified(user);
        },

        markOndagoProUserAsModified(user) {
            this.modifiedOndagoProUsers = new Set([...this.modifiedOndagoProUsers, user.id]);
            this.hasOndagoProChanges = true;
        },

        isOndagoProUserModified(user) {
            return this.modifiedOndagoProUsers.has(user.id) || this.userRoleChanges[user.id];
        },

        markWebviewerAsModified() {
            // Check if there are actual changes compared to original
            const hasAddedUsers = this.loadedUsersForWebviewer.some(user =>
                !this.originalWebviewerUsers.some(orig => orig.email === user.email)
            );
            const hasMarkedForDeletion = this.markedForDeletionWebviewer.size > 0;

            this.hasWebviewerChanges = hasAddedUsers || hasMarkedForDeletion;
        },

        clearWebviewerChanges() {
            this.hasWebviewerChanges = false;
            this.modifiedWebviewerUsers = new Set();
            this.markedForDeletionWebviewer = new Set();
            this.originalWebviewerUsers = JSON.parse(JSON.stringify(this.loadedUsersForWebviewer));
        },

        clearOndagoProChanges() {
            this.hasOndagoProChanges = false;
            this.modifiedOndagoProUsers = new Set();
            this.markedForDeletionOndagoPro = new Set();
            this.userRoleChanges = {};
            this.ondagoPlusMap = {};
            this.ondagoPlusChanges = {};
            this.originalOndagoProUsers = JSON.parse(JSON.stringify(this.loadedUsersForOndagoPro));
            const ids = this.loadedUsersForOndagoPro.map(u => u.id);
            if (ids.length > 0) this.loadOndagoPlusMap(ids);
        },

        loadOndagoPlusMap(userIds) {
            realtimeDatabase.ref('purchasables/freePass').once('value').then((snapshot) => {
                const map = {};
                const freePassData = snapshot.val();
                if (freePassData) {
                    userIds.forEach(userId => {
                        const entry = freePassData[userId];
                        map[userId] = Array.isArray(entry) && entry.includes('com.igloocreations.ondago.plus');
                    });
                }
                this.ondagoPlusMap = map;
            }).catch((error) => {
                console.error('Error loading ondago plus map:', error);
                this.ondagoPlusMap = {};
            });
        },

        handleRemoveOndagoProUser(user) {
            // Mark user for deletion instead of removing immediately
            this.markedForDeletionOndagoPro.add(user.id);
            // Force Vue to detect the change
            this.markedForDeletionOndagoPro = new Set(this.markedForDeletionOndagoPro);
            this.markOndagoProUserAsModified(user);
        },

        handleUndoRemoveOndagoProUser(user) {
            // Unmark user from deletion
            this.markedForDeletionOndagoPro.delete(user.id);
            // Force Vue to detect the change
            this.markedForDeletionOndagoPro = new Set(this.markedForDeletionOndagoPro);

            // Check if user should still be marked as modified
            const wasInOriginalList = this.originalOndagoProUsers.some(u => u.id === user.id);
            if (wasInOriginalList) {
                // User was in original list, check if they have any other modifications
                const originalUser = this.originalOndagoProUsers.find(u => u.id === user.id);
                const hasRoleChange = this.userRoleChanges[user.id] !== undefined;
                const hasDataChanges = originalUser && (
                    originalUser.phoneNumber !== user.phoneNumber ||
                    originalUser.notifyViaAppNotification !== user.notifyViaAppNotification ||
                    originalUser.notifyViaEmail !== user.notifyViaEmail ||
                    originalUser.notifyViaSMS !== user.notifyViaSMS ||
                    JSON.stringify(originalUser.groupIds) !== JSON.stringify(user.groupIds)
                );
                const hasOndagoPlusChange = this.ondagoPlusChanges[user.id] !== undefined;

                if (!hasRoleChange && !hasDataChanges && !hasOndagoPlusChange) {
                    this.modifiedOndagoProUsers = new Set([...this.modifiedOndagoProUsers].filter(id => id !== user.id));
                }
            }

            // Recalculate if there are any changes
            this.hasOndagoProChanges =
                this.modifiedOndagoProUsers.size > 0 ||
                Object.keys(this.userRoleChanges).length > 0 ||
                this.markedForDeletionOndagoPro.size > 0 ||
                this.loadedUsersForOndagoPro.some(u => !this.originalOndagoProUsers.some(orig => orig.id === u.id));
        },

        isOndagoProUserMarkedForDeletion(user) {
            return this.markedForDeletionOndagoPro.has(user.id);
        },

        isValidPhoneNumber(phone) {
            return /^\+[1-9]\d{6,14}$/.test(phone) || /^\d{10}$/.test(phone);
        },

        handleSaveOndagoProUsers() {
            if (!this.selectedPublisher || this.isSavingOndagoPro) return;

            const usersWithInvalidPhone = this.loadedUsersForOndagoPro.filter(
                u => !this.markedForDeletionOndagoPro.has(u.id) && u.phoneNumber && !this.isValidPhoneNumber(u.phoneNumber)
            );
            if (usersWithInvalidPhone.length > 0) {
                this.showOndagoProToastAlert(
                    this.$t('admin.system.publisher_configuration_tool.error_phone_numbers_invalid_save'),
                    'danger'
                );
                return;
            }

            this.isSavingOndagoPro = true;

            const pendingUsers = this.loadedUsersForOndagoPro.filter(
                u => u.pendingCreation && !this.markedForDeletionOndagoPro.has(u.id)
            );

            const resolvePending = pendingUsers.length > 0
                ? Promise.all(pendingUsers.map(user =>
                    this.userService.createUser(user.email).then(newUser => {
                        const uid = (newUser && newUser.uid) ? newUser.uid : '';
                        this.modifiedOndagoProUsers.delete(user.id);
                        user.id = uid;
                        user.email = (newUser && newUser.email) || user.email;
                        user.pendingCreation = false;
                        this.modifiedOndagoProUsers = new Set([...this.modifiedOndagoProUsers, uid]);
                        this.authenticationService.resetPassword(user.email);
                    })
                ))
                : Promise.resolve();

            resolvePending.then(() => {
                const publisherId = this.selectedPublisher.getId();
                const emergencyGroupId = this.selectedPublisher.getContactGroupEmergency();
                const terrainGroupId = this.selectedPublisher.getContactGroupTerrainReport();

                const usersToDelete = this.loadedUsersForOndagoPro.filter(u => this.markedForDeletionOndagoPro.has(u.id));
                const usersToKeep = this.loadedUsersForOndagoPro.filter(u => !this.markedForDeletionOndagoPro.has(u.id));

                const savePromises = [];

                usersToDelete.forEach(user => {
                    const groupIdsToDisable = {};
                    if (emergencyGroupId) groupIdsToDisable[emergencyGroupId] = false;
                    if (terrainGroupId) groupIdsToDisable[terrainGroupId] = false;
                    if (Object.keys(groupIdsToDisable).length > 0) {
                        savePromises.push(this.userService.disableGroupIds(user.id, groupIdsToDisable));
                    }
                    savePromises.push(this.securityService.deleteUserRole(publisherId, user.id));
                    if (this.ondagoPlusMap[user.id]) {
                        savePromises.push(this.userService.updateOndagoPlus(user.id, false));
                    }
                });

                usersToKeep.forEach(user => {
                    if (!this.modifiedOndagoProUsers.has(user.id)) return;
                    savePromises.push(this.userService.saveUser(user.id, user));
                    if (this.userRoleChanges[user.id]) {
                        savePromises.push(
                            this.securityService.saveUserRole(publisherId, user.id, this.userRoleChanges[user.id])
                        );
                    }
                    if (this.ondagoPlusChanges[user.id] !== undefined) {
                        savePromises.push(this.userService.updateOndagoPlus(user.id, this.ondagoPlusChanges[user.id]));
                    }
                });

                return Promise.all(savePromises).then(() => {
                    this.loadedUsersForOndagoPro = usersToKeep;
                    this.clearOndagoProChanges();

                    const userIds = usersToKeep.map(u => u.id);
                    if (userIds.length > 0) {
                        this.securityService.loadUsersRoles(userIds).then((roles) => {
                            this.usersRolesForOndagoPro = roles || {};
                            this.showOndagoProToastAlert(
                                this.$t("admin.system.publisher_configuration_tool.role_update_success"),
                                'success'
                            );
                        }).catch(() => {
                            this.showOndagoProToastAlert(
                                this.$t("admin.system.publisher_configuration_tool.ondago_pro_users_alert_save_successful"),
                                'success'
                            );
                        });
                    } else {
                        this.showOndagoProToastAlert(
                            this.$t("admin.system.publisher_configuration_tool.ondago_pro_users_alert_save_successful"),
                            'success'
                        );
                    }
                });
            }).catch((error) => {
                console.error("Error saving Ondago Pro users:", error);
                const detail = error?.message || error?.code || String(error);
                this.showOndagoProToastAlert(
                    this.$t("admin.system.publisher_configuration_tool.role_update_error") + ' — ' + detail,
                    'danger'
                );
            }).finally(() => {
                this.isSavingOndagoPro = false;
            });
        },

        handleRemoveUserFromPublisher(email) {
            if (this.selectedPublisher) {
                this.markedForDeletionWebviewer = new Set([...this.markedForDeletionWebviewer, email]);
                this.modifiedWebviewerUsers = new Set([...this.modifiedWebviewerUsers, email]);
                this.markWebviewerAsModified();
            }
        },

        handleUndoRemoveUserFromPublisher(email) {
            if (this.selectedPublisher) {
                this.markedForDeletionWebviewer = new Set([...this.markedForDeletionWebviewer].filter(e => e !== email));
                this.markWebviewerAsModified();
            }
        },

        isWebviewerUserMarkedForDeletion(email) {
            return this.markedForDeletionWebviewer.has(email);
        },

        handleSaveUsersForPublisher(publisherId) {
            if (this.isSavingWebviewer) return;
            this.isSavingWebviewer = true;

            const pendingUsers = this.loadedUsersForWebviewer.filter(
                u => u.pendingCreation && !this.markedForDeletionWebviewer.has(u.email)
            );

            const resolvePending = pendingUsers.length > 0
                ? Promise.all(pendingUsers.map(user =>
                    this.userService.createUser(user.email).then(newUser => {
                        user.uid = (newUser && newUser.uid) ? newUser.uid : '';
                        user.email = (newUser && newUser.email) || user.email;
                        user.pendingCreation = false;
                        this.authenticationService.resetPassword(user.email);
                    })
                ))
                : Promise.resolve();

            resolvePending.then(() => {
                this.loadedUsersForWebviewer = this.loadedUsersForWebviewer.filter(user =>
                    !this.markedForDeletionWebviewer.has(user.email)
                );

                const emailsWithoutUID = this.loadedUsersForWebviewer.filter(user => user.uid === "").map(user => user.email);

                if (publisherId) {
                    this.userService.loadUsersIdsByEmails(emailsWithoutUID).then((users) => {
                        if (users) {
                            this.loadedUsersForWebviewer.forEach((user) => {
                                const matchingUser = users.find(u => u.email === user.email);
                                if (matchingUser) {
                                    user.uid = matchingUser.uid;
                                }
                            });
                        }

                        const { allUIDs, emailsWithEmptyUIDs } = this.loadedUsersForWebviewer.reduce((acc, user) => {
                            if (user.uid !== "") {
                                acc.allUIDs.push(user.uid);
                            } else {
                                acc.emailsWithEmptyUIDs.push(user.email);
                            }
                            return acc;
                        }, { allUIDs: [], emailsWithEmptyUIDs: [] });

                        this.publisherService.saveUsersForPublisher(publisherId, allUIDs).then(() => {
                            this.clearWebviewerChanges();
                            if (emailsWithEmptyUIDs.length > 0) {
                                this.showToastAlert(this.$t("admin.system.publisher_configuration_tool.ondago_pro_users_alert_save_partial") + emailsWithEmptyUIDs.join(','), 'warning');
                            } else {
                                this.showToastAlert(this.$t("admin.system.publisher_configuration_tool.ondago_pro_users_alert_save_successful"), 'success');
                            }
                        }).catch(() => {
                            this.showToastAlert(this.$t("admin.system.publisher_configuration_tool.ondago_pro_users_alert_save_failed"), 'danger');
                        }).finally(() => {
                            this.isSavingWebviewer = false;
                        });
                    }).catch(() => {
                        this.showToastAlert(this.$t("admin.system.publisher_configuration_tool.ondago_pro_users_alert_save_failed"), 'danger');
                        this.isSavingWebviewer = false;
                    });
                }
            }).catch(() => {
                this.showToastAlert(this.$t("admin.system.publisher_configuration_tool.create_user_error"), 'danger');
                this.isSavingWebviewer = false;
            });
        },

        handleAddUserFromPublisher() {
            if (this.selectedPublisher) {
                this.emailError = '';
                this.webviewerNotFoundEmail = null;

                if (!this.newUserEmailToAdd) {
                    this.emailError = this.$t("admin.system.publisher_configuration_tool.error_ondago_pro_email_required");
                    return;
                }

                if (!this.validateEmail(this.newUserEmailToAdd)) {
                    this.emailError = this.$t("admin.system.publisher_configuration_tool.error_ondago_pro_email_invalid");
                    return;
                }

                if (this.isEmailAlreadyPresent(this.newUserEmailToAdd)) {
                    this.emailError = this.$t("admin.system.publisher_configuration_tool.error_ondago_pro_user_already_added");
                    return;
                }

                const emailToAdd = this.newUserEmailToAdd;
                // Existence is determined by the Ondago backend, not Firebase's
                // isUserExist (see handleAddOndagoProUser for why).
                this.userService.loadUsersIdsByEmails([emailToAdd]).then((users) => {
                    if (!users || users.length === 0) {
                        this.emailError = this.$t("admin.system.publisher_configuration_tool.error_ondago_pro_user_not_found");
                        this.webviewerNotFoundEmail = emailToAdd;
                        return;
                    }
                    this.loadedUsersForWebviewer.push({ uid: users[0].uid, email: emailToAdd });
                    this.modifiedWebviewerUsers = new Set([...this.modifiedWebviewerUsers, emailToAdd]);
                    this.markWebviewerAsModified();
                    this.newUserEmailToAdd = '';
                }).catch((error) => {
                    console.error('Error loading user by email:', error);
                    this.emailError = this.$t("admin.system.publisher_configuration_tool.error_ondago_pro_user_not_found");
                });
            }
        },

        verifyUserGroupId(user, poiType) {
            if (this.selectedPublisher) {
                try {
                    var groupId = ''
                    if (poiType == 'emergency') {
                        groupId = this.selectedPublisher.getContactGroupEmergency()
                    } else if (poiType == 'terrain_report') {
                        groupId = this.selectedPublisher.getContactGroupTerrainReport()
                    }

                    if (!groupId) {
                        return false;
                    }

                    const userGroupId = user.groupIds[groupId]
                    return userGroupId != null ? userGroupId : false;
                } catch (error) {
                    console.warn('Error verifying user group:', error);
                    return false;
                }
            }
            return false;
        },

        handleUserGroupIdChange(user, poiType, event) {
            if (this.selectedPublisher) {
                try {
                    var groupId = ''
                    if (poiType == 'emergency') {
                        groupId = this.selectedPublisher.getContactGroupEmergency()
                    } else if (poiType == 'terrain_report') {
                        groupId = this.selectedPublisher.getContactGroupTerrainReport()
                    }

                    if (groupId) {
                        user.groupIds[groupId] = event.target.checked
                    }
                } catch (error) {
                    console.warn('Error updating user group:', error);
                }
            }
        },

        validateEmail(email) {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(email);
        },

        isEmailAlreadyPresent(email) {
            return this.loadedUsersForWebviewer.some(user => user.email === email);
        },

        isOndagoProUserAlreadyPresent(email) {
            return this.loadedUsersForOndagoPro.some(user => user.email === email);
        },

        handleAddOndagoProUser() {
            if (this.selectedPublisher) {
                this.ondagoProEmailError = '';
                this.ondagoProNotFoundEmail = null;

                if (!this.newOndagoProUserEmail) {
                    this.ondagoProEmailError = this.$t("admin.system.publisher_configuration_tool.error_ondago_pro_email_required");
                    return;
                }

                if (!this.validateEmail(this.newOndagoProUserEmail)) {
                    this.ondagoProEmailError = this.$t("admin.system.publisher_configuration_tool.error_ondago_pro_email_invalid");
                    return;
                }

                if (this.isOndagoProUserAlreadyPresent(this.newOndagoProUserEmail)) {
                    this.ondagoProEmailError = this.$t("admin.system.publisher_configuration_tool.error_ondago_pro_user_already_added");
                    return;
                }

                const ondagoProEmailToAdd = this.newOndagoProUserEmail;
                // Existence is determined by the Ondago backend (source of truth for
                // Ondago users). We intentionally do NOT use Firebase's
                // fetchSignInMethodsForEmail/isUserExist here: it sends the current page
                // URL as continue_uri and fails with INVALID_CONTINUE_URI whenever the
                // hosting domain belongs to a different Firebase project than the API key.
                this.userService.loadUsersIdsByEmails([ondagoProEmailToAdd]).then((users) => {
                        if (users && users.length > 0) {
                            const userFromAPI = users[0];

                            const initialGroupIds = {};
                            try {
                                const emergencyGroupId = this.selectedPublisher.getContactGroupEmergency();
                                const terrainReportGroupId = this.selectedPublisher.getContactGroupTerrainReport();

                                if (emergencyGroupId) {
                                    initialGroupIds[emergencyGroupId] = false;
                                }
                                if (terrainReportGroupId) {
                                    initialGroupIds[terrainReportGroupId] = false;
                                }
                            } catch (error) {
                                console.warn('Could not initialize group IDs:', error);
                            }

                            this.userService.loadUser(userFromAPI.uid).then((existingUser) => {
                                const existingGroupIds = existingUser ? (existingUser.groupIds || {}) : {};
                                const mergedGroupIds = { ...existingGroupIds };
                                Object.keys(initialGroupIds).forEach(key => {
                                    if (mergedGroupIds[key] === undefined) mergedGroupIds[key] = false;
                                });
                                const newUser = {
                                    id: userFromAPI.uid,
                                    email: userFromAPI.email,
                                    phoneNumber: existingUser ? (existingUser.phoneNumber || "") : "",
                                    groupIds: mergedGroupIds,
                                    notifyViaAppNotification: existingUser ? (existingUser.notifyViaAppNotification || false) : false,
                                    notifyViaEmail: existingUser ? (existingUser.notifyViaEmail || false) : false,
                                    notifyViaSMS: existingUser ? (existingUser.notifyViaSMS || false) : false
                                };

                                this.loadedUsersForOndagoPro.push(newUser);
                                this.modifiedOndagoProUsers = new Set([...this.modifiedOndagoProUsers, newUser.id]);
                                this.hasOndagoProChanges = true;

                                const allUserIds = this.loadedUsersForOndagoPro.map(u => u.id);
                                if (allUserIds.length > 0) {
                                    this.securityService.loadUsersRoles(allUserIds).then((roles) => {
                                        this.usersRolesForOndagoPro = roles || {};
                                    }).catch((error) => {
                                        console.error('Error loading roles:', error);
                                        this.usersRolesForOndagoPro = {};
                                    });
                                }

                                this.newOndagoProUserEmail = '';
                            }).catch(() => {
                                const newUser = {
                                    id: userFromAPI.uid,
                                    email: userFromAPI.email,
                                    phoneNumber: "",
                                    groupIds: initialGroupIds,
                                    notifyViaAppNotification: false,
                                    notifyViaEmail: false,
                                    notifyViaSMS: false
                                };
                                this.loadedUsersForOndagoPro.push(newUser);
                                this.modifiedOndagoProUsers = new Set([...this.modifiedOndagoProUsers, newUser.id]);
                                this.hasOndagoProChanges = true;
                                this.newOndagoProUserEmail = '';
                            });
                        } else {
                            this.ondagoProEmailError = this.$t("admin.system.publisher_configuration_tool.error_ondago_pro_user_not_found");
                            this.ondagoProNotFoundEmail = ondagoProEmailToAdd;
                        }
                    }).catch((error) => {
                        console.error('Error loading user by email:', error);
                        this.ondagoProEmailError = this.$t("admin.system.publisher_configuration_tool.error_ondago_pro_user_not_found");
                    });
            }
        },

        handleCreateAndAddWebviewerUser() {
            const email = this.webviewerNotFoundEmail;
            if (!email) return;

            this.loadedUsersForWebviewer.push({ uid: '', email, pendingCreation: true });
            this.modifiedWebviewerUsers = new Set([...this.modifiedWebviewerUsers, email]);
            this.markWebviewerAsModified();
            this.webviewerNotFoundEmail = null;
            this.emailError = '';
            this.newUserEmailToAdd = '';
            this.showToastAlert(this.$t("admin.system.publisher_configuration_tool.create_user_pending"), 'info');
        },

        handleCreateAndAddOndagoProUser() {
            const email = this.ondagoProNotFoundEmail;
            if (!email) return;

            const initialGroupIds = {};
            try {
                const emergencyGroupId = this.selectedPublisher.getContactGroupEmergency();
                const terrainReportGroupId = this.selectedPublisher.getContactGroupTerrainReport();
                if (emergencyGroupId) initialGroupIds[emergencyGroupId] = false;
                if (terrainReportGroupId) initialGroupIds[terrainReportGroupId] = false;
            } catch (error) {
                console.warn('Could not initialize group IDs:', error);
            }

            const newUserObj = {
                id: email,
                email,
                phoneNumber: '',
                groupIds: initialGroupIds,
                notifyViaAppNotification: false,
                notifyViaEmail: false,
                notifyViaSMS: false,
                pendingCreation: true
            };

            this.loadedUsersForOndagoPro.push(newUserObj);
            this.modifiedOndagoProUsers = new Set([...this.modifiedOndagoProUsers, email]);
            this.hasOndagoProChanges = true;
            this.ondagoProNotFoundEmail = null;
            this.ondagoProEmailError = '';
            this.newOndagoProUserEmail = '';
            this.showOndagoProToastAlert(this.$t("admin.system.publisher_configuration_tool.create_user_pending"), 'info');
        },

        handlePublisherSelected(publisher) {
            // Check for unsaved changes before switching
            if (this.hasWebviewerChanges || this.hasOndagoProChanges) {
                const confirmSwitch = confirm(this.$t("admin.system.publisher_configuration_tool.confirm_switch_publisher"));
                if (!confirmSwitch) {
                    return;
                }
            }

            this.selectedPublisher = publisher;
            // Set the search query to show the selected publisher name
            this.searchQuery = this.publisherName(publisher);

            if (this.selectedPublisher) {
                this.loadedUsersForOndagoPro = [];
                this.usersRolesForOndagoPro = {};
                this.clearOndagoProChanges();

                const contactGroupByPOIType = this.selectedPublisher.getContactGroupByPOIType()
                if (contactGroupByPOIType && contactGroupByPOIType.length > 0) {
                    this.isLoadingOndagoProUsers = true;
                    this.userService.loadUsersForGroupIds(contactGroupByPOIType).then((users) => {
                        if (!users || users.length === 0) {
                            this.loadedUsersForOndagoPro = [];
                            this.usersRolesForOndagoPro = {};
                            this.isLoadingOndagoProUsers = false;
                            return;
                        }

                        const usersOndagoProIds = users.map(user => user.id);

                        this.securityService.loadUsersRoles(usersOndagoProIds).then((roles) => {
                            this.usersRolesForOndagoPro = roles;
                            this.loadedUsersForOndagoPro = users;
                            this.originalOndagoProUsers = JSON.parse(JSON.stringify(users));
                        }).catch((error) => {
                            console.error("Error loading roles:", error);
                            this.usersRolesForOndagoPro = {};
                            this.loadedUsersForOndagoPro = users;
                            this.originalOndagoProUsers = JSON.parse(JSON.stringify(users));
                        }).finally(() => {
                            this.isLoadingOndagoProUsers = false;
                            this.loadOndagoPlusMap(usersOndagoProIds);
                        });
                    }).catch((error) => {
                        console.error("Error loading users for group IDs:", error);
                        this.loadedUsersForOndagoPro = [];
                        this.usersRolesForOndagoPro = {};
                        this.isLoadingOndagoProUsers = false;
                    })
                }

                const userIds = publisher.getUsersIds()
                if (userIds && userIds.length > 0) {
                    this.isLoadingWebviewerUsers = true;
                    this.userService.loadUsersEmailByIds(userIds).then((users) => {
                        this.loadedUsersForWebviewer = users;
                        this.clearWebviewerChanges();
                    }).catch((error) => {
                        console.error("Error loading users for webviewer:", error)
                    }).finally(() => {
                        this.isLoadingWebviewerUsers = false;
                    });
                } else {
                    this.loadedUsersForWebviewer = [];
                    this.clearWebviewerChanges();
                }
            }
        },

        clearPublisherSelection() {
            // Check for unsaved changes before clearing
            if (this.hasWebviewerChanges || this.hasOndagoProChanges) {
                const confirmSwitch = confirm(this.$t("admin.system.publisher_configuration_tool.confirm_switch_publisher"));
                if (!confirmSwitch) {
                    return;
                }
            }

            this.selectedPublisher = null;
            this.searchQuery = '';
            this.loadedUsersForWebviewer = [];
            this.loadedUsersForOndagoPro = [];
            this.usersRolesForOndagoPro = {};
            this.clearWebviewerChanges();
            this.clearOndagoProChanges();
        },

        _loadAllPublishers() {
            this.publisherService.loadPublishers().then((publishers) => {
                if (publishers) {
                    this.publishers = publishers;
                }
            }).catch((error) => {
                console.log(error);
            });
        },

        showToastAlert(alertMessage, alertType) {
            this.alertMessage = alertMessage;
            this.alertType = alertType;
            this.showAlert = true;
            setTimeout(() => {
                this.showAlert = false;
            }, 5000);
        },

        showOndagoProToastAlert(alertMessage, alertType) {
            this.ondagoProAlertMessage = alertMessage;
            this.ondagoProAlertType = alertType;
            this.showOndagoProAlert = true;
            setTimeout(() => {
                this.showOndagoProAlert = false;
            }, 5000);
        },

        findRolesByUserId(userId) {
            if (this.usersRolesForOndagoPro && this.selectedPublisher) {
                const paths = this.usersRolesForOndagoPro[userId];
                if (paths && paths.length > 0) {
                    return this.getFirstRoleByName(paths, this.selectedPublisher.id);
                }
            }
            return null;
        },

        /**
         * Extracts the first role name from role paths
         * Expected path formats:
         *   - /publishers/E47/role/admin
         *   - /publishers/E47/bundles/E47EcoSentiers/role/admin
         * @param {Array<string>} paths - Array of role paths
         * @param {string} name - Publisher ID to match
         * @returns {string|null} - Role name ('admin' or 'member') or null if not found
         */
        getFirstRoleByName(paths, name) {
            for (const path of paths) {
                const regex = new RegExp(`/publishers/${name}/(?:.+?/)?role/([^/]+)`);
                const match = path.match(regex);

                if (match) {
                    return match[1];
                }
            }
            return null;
        },

        handleBeforeUnload(event) {
            if (this.hasWebviewerChanges || this.hasOndagoProChanges) {
                event.preventDefault();
                event.returnValue = '';
                return '';
            }
        }
    },

    beforeRouteLeave(to, from, next) {
        if (this.hasWebviewerChanges || this.hasOndagoProChanges) {
            const answer = window.confirm(this.$t("admin.system.publisher_configuration_tool.confirm_leave_unsaved"));
            if (answer) {
                next();
            } else {
                next(false);
            }
        } else {
            next();
        }
    },

    mounted() {
        // Warn before closing/refreshing with unsaved changes
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    },

    beforeDestroy() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }
}
</script>

<style scoped>
.container {
    display: flex;
    flex-direction: column;
    margin: 0px;
    width: 100%;
    max-width: none;
    text-align: left;
    min-height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding-bottom: 80px;
}

.toolbar {
    background-color: #ffffff;
    margin-bottom: 8px;
    padding: 8px;
    padding-left: 24px;
    text-align: left;
    width: 100%;
    flex-shrink: 0;
}

.toolbar img {
    margin-right: 16px;
    width: 50px;
}

.toolbar .title {
    display: inline-block;
    font-size: 16px;
    font-weight: bold;
    margin-right: 36px;
    max-width: 250px;
}

/* Change tracking styles */
.modified-row {
    background-color: #fff3cd !important;
    border-left: 4px solid #ffc107;
    transition: all 0.3s ease;
}

.modified-indicator {
    color: #ffc107;
    font-size: 12px;
    margin-right: 8px;
    animation: pulse 2s infinite;
    font-weight: bold;
}

/* Deleted row styles */
.deleted-row {
    background-color: #f8d7da !important;
    border-left: 4px solid #dc3545;
    transition: all 0.3s ease;
}

.deleted-text {
    text-decoration: line-through;
    opacity: 0.6;
}

.deleted-indicator {
    color: #dc3545;
    font-size: 12px;
    margin-right: 8px;
    font-weight: bold;
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
}

.unsaved-changes-banner {
    background-color: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 8px;
    padding: 12px 20px;
    margin-bottom: 16px;
    text-align: center;
    font-weight: 500;
    color: #856404;
    animation: slideDown 0.3s ease;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.warning-icon {
    margin-right: 8px;
    font-size: 18px;
}

.save-section {
    margin-top: 16px;
}

.btn:disabled {
    cursor: not-allowed;
    opacity: 0.65;
}

.btn-warning {
    animation: buttonPulse 2s infinite;
    font-weight: 600;
}

@keyframes buttonPulse {
    0%, 100% {
        box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.7);
    }
    50% {
        box-shadow: 0 0 0 10px rgba(255, 193, 7, 0);
    }
}

.btn-success:disabled {
    background-color: #6c757d;
    border-color: #6c757d;
}

/* Search Bar */
.publisher-search-container {
    background-color: #ffffff;
    padding: 16px 24px;
    margin-bottom: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    position: relative;
    flex-shrink: 0;
}

.publisher-search-input {
    border: none;
    border-bottom: 2px solid #e0e0e0;
    font-size: 18px;
    padding: 8px 0;
    padding-right: 40px;
    width: 100%;
    outline: none;
    transition: border-color 0.2s ease;
}

.publisher-search-input:focus {
    border-bottom-color: #0d6efd;
}

.publisher-search-input:read-only {
    background-color: #f8f9fa;
    cursor: default;
}

.clear-selection-btn {
    position: absolute;
    right: 24px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    font-size: 24px;
    color: #6c757d;
    cursor: pointer;
    padding: 4px 8px;
    line-height: 1;
    transition: color 0.2s ease;
}

.clear-selection-btn:hover {
    color: #dc3545;
}

/* Cards Grid */
.publisher-cards-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    padding: 16px 24px;
    padding-bottom: 40px;
    align-content: flex-start;
}

/* Individual Card */
.publisher-card {
    background-color: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    padding: 16px;
    width: 280px;
    transition: all 0.2s ease;
}

.publisher-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
}

.selected-card {
    background-color: #fff3cd;
    border-left: 4px solid #ffc107;
    border-color: #ffc107;
}

/* Card Content */
.publisher-card-name {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 4px;
    color: #212529;
}

.publisher-card-id {
    font-size: 14px;
    color: #6c757d;
    margin-bottom: 12px;
}

.publisher-card-stats {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.stat-badge {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 12px;
    padding: 4px 10px;
    font-size: 13px;
    color: #495057;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.stat-icon {
    font-size: 14px;
}

/* Empty State */
.publisher-empty-state {
    background-color: #f8f9fa;
    border: 1px dashed #dee2e6;
    border-radius: 8px;
    padding: 32px;
    text-align: center;
    color: #6c757d;
    margin: 16px 24px;
}

.publisher-empty-state p {
    margin: 0;
    font-size: 16px;
}

/* User not found suggestion */
.user-not-found-suggestion {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 14px;
    background-color: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 6px;
    font-size: 14px;
    color: #856404;
}

/* User management cards */
.card {
    margin: 16px 24px;
    margin-bottom: 32px;
}

.card:last-of-type {
    margin-bottom: 80px;
}

/* Ensure horizontal rules have proper spacing */
hr {
    margin: 24px 0;
}

/* Table scrolling for large lists */
.table {
    margin-bottom: 0;
    width: 100%;
}

.table thead {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: white;
}

.table-bordered {
    border-collapse: separate;
    border-spacing: 0;
}

/* Responsive */
@media (max-width: 768px) {
    .publisher-card {
        width: 240px;
    }
}

@media (max-width: 480px) {
    .publisher-card {
        width: 100%;
        max-width: 400px;
    }

    .publisher-cards-grid {
        padding: 16px;
    }

    .publisher-search-container {
        padding: 12px 16px;
    }
}

</style>